// Localização: frontend/src/components/LandingPage/HeroSection.jsx (versão 2.0.0 - Design Elegante e Moderno)
import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Play, Shield, TrendingUp, Heart, CheckCircle2, Sparkles } from 'lucide-react'; 
import { Link } from 'react-router-dom'; 

// Caminho para a imagem de fundo com as ondas verdes
const BACKGROUND_IMAGE_PATH = '/images/fundo_do_Hero.png'; 

// Estatísticas rápidas em destaque
const quickStats = [
    {
        value: '40%',
        label: 'Redução no tempo',
        sublabel: 'de documentação',
        icon: TrendingUp,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
    },
    {
        value: '85%',
        label: 'Aumento na satisfação',
        sublabel: 'dos pacientes',
        icon: Heart,
        color: 'text-pink-600',
        bgColor: 'bg-pink-50'
    },
    {
        value: '',
        label: 'Recursos',
        sublabel: 'com IA',
        icon: Sparkles,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
    }
];

// Badges de confiança
const trustBadges = [
    { text: 'LGPD Compliant', icon: Shield },
    { text: '100% Seguro', icon: CheckCircle2 },
    { text: 'Backup Automático', icon: CheckCircle2 }
];

const HeroSection = () => {

    return (
        // Background com gradiente futurista
        <section 
            className="relative overflow-hidden pt-16 pb-16 sm:pt-20 sm:pb-20 lg:pt-16 lg:pb-16"
            style={{ 
                backgroundImage: `url(${BACKGROUND_IMAGE_PATH})`, 
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            {/* Overlay com gradiente sutil */}
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-white/60 via-white/50 to-white/70 pointer-events-none"></div>

            {/* Padrão decorativo sutil */}
            <div className="absolute inset-0 z-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, #1A5B4C 1px, transparent 0)`,
                    backgroundSize: '50px 50px'
                }} />
            </div>

            {/* Container Principal */}
            <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
                
                {/* 1. Área de Título e Chamada (Texto Principal) */}
                <div className="max-w-4xl mx-auto text-center mb-16 lg:mb-20">
                    {/* Título com animação */}
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight text-gray-900 mb-6"
                    >
                        Sistema de Gestão Completa para{' '}
                        <span className="bg-gradient-to-r from-higeia-dark via-higeia-medium to-higeia-dark bg-clip-text text-transparent">
                            Lar de Idosos e Home Care
                        </span>
                    </motion.h1>
                    
                    {/* Subtítulo com animação */}
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="text-xl sm:text-2xl font-normal text-gray-700 max-w-2xl mx-auto mb-8"
                    >
                        Otimize sua clínica com nossa plataforma intuitiva e baseada em dados.
                    </motion.p>

                    {/* CTAs com animação */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                    >
                        <Link to="/demonstracao">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="flex items-center gap-2 px-6 py-4 text-lg font-semibold text-gray-700 bg-white/80 backdrop-blur-sm border-2 border-gray-200 hover:border-higeia-dark rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                <Play className="h-5 w-5 text-higeia-dark" />
                                Solicitar Demonstração
                            </motion.button>
                        </Link>
                    </motion.div>
                </div>

                {/* 2. Estatísticas Rápidas em Destaque */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="grid grid-cols-3 md:grid-cols-3 gap-2 sm:gap-4 md:gap-6 mb-8 max-w-4xl mx-auto">
                        {quickStats.map((stat, index) => {
                            const Icon = stat.icon;
                            const isRecursosLink = index === 2; // O terceiro card (Automatize com IA) será clicável
                            
                            const content = (
                                <>
                                    {/* Ícone */}
                                    <div className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl ${stat.bgColor} mb-2 sm:mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                        <Icon className={`h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 ${stat.color}`} />
                                    </div>

                                    {/* Valor */}
                                    <div className="mb-1 sm:mb-2 min-h-[1.5rem] sm:min-h-[2rem] md:min-h-[2.5rem]">
                                        {stat.value && (
                                            <span className="text-lg sm:text-2xl md:text-4xl font-black text-gray-900">{stat.value}</span>
                                        )}
                                    </div>

                                    {/* Labels */}
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <p className={`${stat.value ? 'text-xs sm:text-sm' : 'text-sm sm:text-base md:text-xl'} font-bold text-gray-900 leading-tight`}>{stat.label}</p>
                                            <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">{stat.sublabel}</p>
                                        </div>
                                        {isRecursosLink && (
                                            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0" />
                                        )}
                                    </div>

                                    {/* Efeito de brilho no hover */}
                                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-higeia-light/0 to-higeia-light/0 group-hover:from-higeia-light/10 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
                                </>
                            );
                            
                            if (isRecursosLink) {
                                return (
                                    <Link key={index} to="/recursos">
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.7 + index * 0.15, duration: 0.5 }}
                                            whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                            className="group relative bg-white/90 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50 cursor-pointer h-full flex flex-col"
                                        >
                                            {content}
                                        </motion.div>
                                    </Link>
                                );
                            }
                            
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.7 + index * 0.15, duration: 0.5 }}
                                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                    className="group relative bg-white/90 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50 h-full flex flex-col"
                                >
                                    {content}
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Badges de Confiança */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.1, duration: 0.6 }}
                        className="flex flex-wrap justify-center items-center gap-4"
                    >
                        {trustBadges.map((badge, index) => {
                            const Icon = badge.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 1.2 + index * 0.1, duration: 0.4 }}
                                    className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
                                >
                                    <Icon className="h-4 w-4 text-higeia-dark" />
                                    <span className="text-sm font-medium text-gray-700">{badge.text}</span>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default HeroSection;

